/* eslint react/jsx-props-no-spreading: 0 */

import { CircularProgress, Divider, Grid, Stack, Typography } from '@mui/material';
import {
    Accordion,
    Autocomplete,
    backendServices,
    DeliveryMethod,
    FormStepComponentProps,
    PasswordField,
    PasswordRules,
    PurchaseOrderFileFormat,
    RadioButton,
    RadioButtonGroup,
    SignVATInvoices,
    SortedSelect,
    TextField,
    useBuyerPortalSettings,
    useCountries,
    useCountryAndStateOptions,
    useCurrency,
    useCurrencyOptions,
} from 'common';
import { useEffect, useState } from 'react';
import { Controller, useFormContext, useWatch } from 'react-hook-form';
import { CompanyLogo } from '../../../components';
import { useCompanyPublicData } from '../../../services';
import { SupplierSignUpSchema } from './SupplierSignUpSchema';
import { useVatArchiveService } from './useVatArchiveService';

type SupplierSignUpStepProps = FormStepComponentProps<typeof SupplierSignUpSchema>;

type YourCustomerProps = SupplierSignUpStepProps & {
    companyToken: string;
    submissionMethod: Pick<
        backendServices.ViewModels.SubmissionMethodViewModel,
        'Id' | 'Name' | 'SubmissionMethod'
    > | null;
    isCloudCapture: boolean;
};

const DeliveryMethodOptions = [
    {
        label: DeliveryMethod[DeliveryMethod.Email],
        value: DeliveryMethod.Email,
    },
    {
        label: DeliveryMethod[DeliveryMethod.FTP],
        value: DeliveryMethod.FTP,
    },
];

function shouldDisplayVatFields(vatArchiveServiceData: backendServices.ViewModels.VatArchiveServiceViewModel[]) {
    return (
        vatArchiveServiceData.find(
            ({ BuyerDestinationProfileId, InvoiceProfileId, VATComplianceRequired }) =>
                BuyerDestinationProfileId === null && InvoiceProfileId === null && !!VATComplianceRequired
        ) != null
    );
}

export function YourCustomer({ control, companyToken, submissionMethod, isCloudCapture }: YourCustomerProps) {
    const { setValue } = useFormContext();
    const selectedPODeliveryMethod =
        useWatch({ name: 'customer.poDelivery.deliveryMethod', control, exact: true }) ?? null;

    const vatComplianceRequired =
        useWatch({ name: 'customer.vatInformation.VATComplianceRequired', control, exact: true }) ?? false;

    const invoiceCurrency = useWatch({ name: 'customer.invoiceCurrency', control, exact: true }) as
        | number
        | null
        | undefined;

    const { data: company, isLoading: isLoadingCompany } = useCompanyPublicData({ search: companyToken });
    const { data: vatArchiveServiceData = [], isLoading: isLoadingVatData } = useVatArchiveService({
        companyId: company?.ID,
    });

    const displayVatFields = isLoadingVatData ? true : shouldDisplayVatFields(vatArchiveServiceData);

    const { data: buyerPortalSettings, isLoading: isLoadingBuyerPortalSettings } = useBuyerPortalSettings({
        companyId: company?.ID ?? null,
    });

    const isPODeliveryEnabled = buyerPortalSettings?.EnablePODelivery ?? true;

    useEffect(() => {
        if (!isPODeliveryEnabled) {
            setValue('customer.poDelivery', null);
        }
    }, [isPODeliveryEnabled, setValue]);

    useEffect(() => {
        if (!displayVatFields) {
            setValue('customer.vatInformation', null);
        }
    }, [displayVatFields, setValue]);

    const { data: currencyList = [], isLoading } = useCurrency();

    const currencyOptions = useCurrencyOptions(currencyList);

    const [usesMultipleCurrencies, setUsesMultipleCurrencies] = useState(
        invoiceCurrency === null || invoiceCurrency === 0
    );

    const currencyControl = isLoading ? (
        <Stack direction="row" justifyContent="flex-start" alignItems="center" spacing={1}>
            <CircularProgress size="20px" />
        </Stack>
    ) : (
        <Grid container spacing={3} alignItems="flex-start">
            <Grid item xs={12} md={6}>
                <Stack>
                    <RadioButtonGroup
                        testId="multiple-currency"
                        label="Will your invoices be billed in the same currency?"
                        value={usesMultipleCurrencies ? 'true' : 'false'}
                        onChange={(e) => {
                            const value = e.target.value;

                            const usesMultiple = value === 'true';

                            setValue('customer.invoiceCurrency', usesMultiple ? null : undefined, {
                                shouldValidate: usesMultiple,
                            });

                            setUsesMultipleCurrencies(usesMultiple);
                        }}
                        required
                    >
                        <RadioButton disabled={isCloudCapture} label="Yes, one currency" value="false" />
                        <RadioButton disabled={isCloudCapture} label="No, multiple currencies" value="true" />
                    </RadioButtonGroup>
                </Stack>
            </Grid>
            {!usesMultipleCurrencies && (
                <Grid
                    item
                    xs={12}
                    md={6}
                    mt={{
                        md: 3,
                    }}
                >
                    <Controller
                        name="customer.invoiceCurrency"
                        control={control}
                        render={({ field, fieldState }) => (
                            <Autocomplete
                                id="invoiceCurrency"
                                label="Invoice Currency"
                                required={!usesMultipleCurrencies}
                                error={fieldState.invalid}
                                helperText={fieldState.error?.message}
                                value={field.value}
                                options={currencyOptions}
                                onChange={field.onChange}
                                testId="currency-autocomplete"
                                disabled={usesMultipleCurrencies || isCloudCapture}
                                disableSorting
                            />
                        )}
                    />
                </Grid>
            )}
        </Grid>
    );

    const PODeliveryControls = isLoadingBuyerPortalSettings ? (
        <Stack direction="row" justifyContent="flex-start" alignItems="center" spacing={1}>
            <CircularProgress size="20px" />
        </Stack>
    ) : (
        <Accordion
            title="Purchase Order Delivery"
            variant="transparent"
            typographyProps={{
                variant: 'subtitle1',
            }}
            testId="po-delivery-accordion"
            // TODO: remove and make it auto-expand on form error
            defaultExpanded
        >
            <Stack spacing={3}>
                <Controller
                    name="customer.poDelivery.deliveryMethod"
                    control={control}
                    render={({ field, fieldState }) => (
                        <SortedSelect
                            {...field}
                            value={DeliveryMethodOptions.find((opt) => opt.value === field.value) ?? null}
                            onChange={(selection) => {
                                field.onChange(selection?.value ?? null);
                                if (selection?.value !== DeliveryMethod.Email) {
                                    setValue('customer.poDelivery.deliveryEmail', undefined, { shouldValidate: true });
                                }
                            }}
                            id="poDeliveryMethod"
                            label="Delivery Method"
                            required
                            error={fieldState.invalid}
                            helperText={fieldState.error?.message}
                            options={DeliveryMethodOptions}
                            testId="po-delivery-method"
                            disabled={isCloudCapture}
                        />
                    )}
                />
                {selectedPODeliveryMethod === DeliveryMethod.Email && (
                    <Controller
                        name="customer.poDelivery.deliveryEmail"
                        control={control}
                        render={({ field, fieldState }) => (
                            <TextField
                                {...field}
                                label="Delivery Email Address"
                                required
                                error={fieldState.invalid}
                                helperText={fieldState.error?.message}
                                testId="po-delivery-email"
                                disabled={isCloudCapture}
                            />
                        )}
                    />
                )}
                <Controller
                    name="customer.poDelivery.deliveryFileFormat"
                    control={control}
                    render={({ field, fieldState }) => (
                        <SortedSelect
                            {...field}
                            id="poDeliveryFileFormat"
                            label="File Format"
                            required
                            error={fieldState.invalid}
                            helperText={fieldState.error?.message}
                            options={PurchaseOrderFileFormat}
                            testId="po-delivery-file-format"
                            disabled={isCloudCapture}
                        />
                    )}
                />
            </Stack>
        </Accordion>
    );

    const vatControls = isLoadingVatData ? (
        <Stack direction="row" justifyContent="flex-start" alignItems="center" spacing={1}>
            <CircularProgress size="20px" />
        </Stack>
    ) : (
        <Accordion
            title="VAT Information"
            variant="transparent"
            typographyProps={{
                variant: 'subtitle1',
            }}
            testId="vat-information-accordion"
            // TODO: remove and make it auto-expand on form error
            defaultExpanded
        >
            <Grid container spacing={3} alignItems="center">
                <Grid item xs={12} md={6}>
                    <Stack>
                        <Controller
                            name="customer.vatInformation.VATComplianceRequired"
                            control={control}
                            render={({ field, fieldState }) => (
                                <RadioButtonGroup
                                    testId="vat-compliance-required"
                                    label="Are you registered for VAT?"
                                    value={field.value ? 'true' : 'false'}
                                    onChange={(e) => {
                                        const value = e.target.value;

                                        const isRegistered = value === 'true';
                                        field.onChange(isRegistered);
                                        setValue('customer.vatInformation.VATNumber', isRegistered ? '' : undefined, {
                                            shouldValidate: !isRegistered,
                                        });
                                    }}
                                    required
                                    error={fieldState.invalid}
                                    helperText={fieldState.error?.message}
                                    row
                                >
                                    <RadioButton disabled={isCloudCapture} label="Yes" value="true" />
                                    <RadioButton disabled={isCloudCapture} label="No" value="false" />
                                </RadioButtonGroup>
                            )}
                        />
                    </Stack>
                </Grid>
                <Grid item xs={12} md={6}>
                    <Controller
                        name="customer.vatInformation.VATNumber"
                        control={control}
                        render={({ field, fieldState }) => (
                            <TextField
                                {...field}
                                value={field.value ?? ''}
                                label="VAT Registration Number"
                                required={vatComplianceRequired}
                                disabled={!vatComplianceRequired}
                                error={fieldState.invalid}
                                helperText={fieldState.error?.message}
                                testId="vat-number"
                            />
                        )}
                    />
                </Grid>
                <Grid item xs={12}>
                    <Stack>
                        <Controller
                            name="customer.vatInformation.SignVATInvoices"
                            control={control}
                            render={({ field, fieldState }) => (
                                <RadioButtonGroup
                                    testId="sign-vat-invoices"
                                    label="Who will sign VAT invoices?"
                                    value={field.value?.toString() ?? SignVATInvoices.Both.toString()}
                                    onChange={(e) => {
                                        const value = e.target.value;
                                        field.onChange(Number(value));
                                    }}
                                    required
                                    error={fieldState.invalid}
                                    helperText={fieldState.error?.message}
                                >
                                    <RadioButton
                                        label="Your Organization"
                                        value={SignVATInvoices.Supplier.toString()}
                                    />
                                    <RadioButton label="Transcepta" value={SignVATInvoices.Transcepta.toString()} />
                                    <RadioButton label="Both (Recommended)" value={SignVATInvoices.Both.toString()} />
                                </RadioButtonGroup>
                            )}
                        />
                    </Stack>
                </Grid>
            </Grid>
        </Accordion>
    );

    return (
        <Stack spacing={3} p={{ xs: 2, sm: 0 }} divider={<Divider />}>
            <Accordion
                title="Confirm Customer"
                variant="transparent"
                expanded
                typographyProps={{
                    variant: 'subtitle1',
                }}
            >
                <Stack spacing={3} direction="row" alignItems="center">
                    {isLoadingCompany ? (
                        <Stack direction="row" justifyContent="flex-start" alignItems="center" spacing={1}>
                            <CircularProgress size="20px" />
                        </Stack>
                    ) : (
                        <>
                            <CompanyLogo
                                src={company?.Logo ?? ''}
                                sx={{
                                    maxWidth: '100px',
                                    maxHeight: '100px',
                                }}
                            />
                            <Typography variant="h1" data-testid="company-name">
                                {company?.Name ?? ''}
                            </Typography>
                        </>
                    )}
                </Stack>
            </Accordion>
            <Accordion
                title="Customer Invoicing"
                variant="transparent"
                typographyProps={{
                    variant: 'subtitle1',
                }}
                // TODO: remove and make it auto-expand on form error
                defaultExpanded
            >
                {submissionMethod != null && (
                    <Stack spacing={3} mb={3}>
                        <Typography variant="caption">
                            Invoice Submission Method:{' '}
                            <Typography component="span" variant="caption">
                                {submissionMethod?.Name}
                            </Typography>
                        </Typography>
                    </Stack>
                )}
                {currencyControl}
            </Accordion>
            {(!isCloudCapture || selectedPODeliveryMethod != null) && isPODeliveryEnabled && PODeliveryControls}
            {!isCloudCapture && displayVatFields && vatControls}
        </Stack>
    );
}

export function YourBusiness(props: SupplierSignUpStepProps) {
    const { control } = props;
    const { setValue, clearErrors } = useFormContext();
    const [countryHasStates, setCountryHasStates] = useState(false);

    const selectedCountryCode = useWatch({ name: 'business.country', control, exact: true }) ?? null;

    const { data: countryList = [], isLoading: isLoadingCountries } = useCountries();

    const { countryOptions, stateOptions } = useCountryAndStateOptions({ selectedCountryCode, countryList });

    const handleCountrySelect = (value: string | null) => {
        clearErrors('business.country');
        clearErrors('business.state');
        setValue('business.country', value, { shouldDirty: true });
        setValue('business.state', '', { shouldDirty: true });
    };

    useEffect(() => {
        const hasStates = Object.keys(stateOptions).length > 0;

        setCountryHasStates(hasStates);
        setValue('business.countryHasStates', hasStates);
    }, [stateOptions, setValue]);

    return (
        <Stack spacing={3} p={{ xs: 2, sm: 0 }} divider={<Divider />}>
            <Accordion
                title="Business Info"
                variant="transparent"
                expanded
                typographyProps={{
                    variant: 'subtitle1',
                }}
            >
                <Stack spacing={3}>
                    <Controller
                        name="business.businessName"
                        control={control}
                        render={({ field, fieldState }) => (
                            <TextField
                                {...field}
                                label="Company Name"
                                required
                                error={fieldState.invalid}
                                helperText={fieldState.error?.message}
                                testId="business-name"
                            />
                        )}
                    />
                </Stack>
            </Accordion>
            <Accordion
                title="Billing Address"
                variant="transparent"
                typographyProps={{
                    variant: 'subtitle1',
                }}
                // TODO: remove and make it auto-expand on form error
                defaultExpanded
            >
                <Stack spacing={3}>
                    {isLoadingCountries ? (
                        <Stack direction="row" justifyContent="flex-start" alignItems="center" spacing={1}>
                            <CircularProgress size="20px" />
                        </Stack>
                    ) : (
                        <Controller
                            name="business.country"
                            control={control}
                            render={({ field, fieldState }) => (
                                <Autocomplete
                                    id="country"
                                    label="Country"
                                    required
                                    error={fieldState.invalid}
                                    helperText={fieldState.error?.message}
                                    value={field.value}
                                    options={countryOptions}
                                    onChange={handleCountrySelect}
                                    testId="country-autocomplete"
                                    disableSorting
                                />
                            )}
                        />
                    )}

                    <Controller
                        name="business.address1"
                        control={control}
                        render={({ field, fieldState }) => (
                            <TextField
                                {...field}
                                label="Address 1"
                                required
                                error={fieldState.invalid}
                                helperText={fieldState.error?.message}
                                testId="address-1"
                            />
                        )}
                    />
                    <Controller
                        name="business.address2"
                        control={control}
                        render={({ field, fieldState }) => (
                            <TextField
                                {...field}
                                label="Address 2"
                                error={fieldState.invalid}
                                helperText={fieldState.error?.message}
                                testId="address-2"
                            />
                        )}
                    />
                    <Controller
                        name="business.address3"
                        control={control}
                        render={({ field, fieldState }) => (
                            <TextField
                                {...field}
                                label="Address 3"
                                error={fieldState.invalid}
                                helperText={fieldState.error?.message}
                                testId="address-3"
                            />
                        )}
                    />

                    <Controller
                        name="business.city"
                        control={control}
                        render={({ field, fieldState }) => (
                            <TextField
                                {...field}
                                label="City"
                                required
                                error={fieldState.invalid}
                                helperText={fieldState.error?.message}
                                testId="city"
                            />
                        )}
                    />
                    <Controller
                        name="business.state"
                        control={control}
                        render={({ field, fieldState }) => {
                            if (!countryHasStates) {
                                return (
                                    <TextField
                                        {...field}
                                        label="State/Region"
                                        error={fieldState.invalid}
                                        helperText={fieldState.error?.message}
                                        disabled={!selectedCountryCode}
                                        testId="state/province/region"
                                        onChange={field.onChange}
                                    />
                                );
                            }

                            return (
                                <Autocomplete
                                    id="state"
                                    label="State/Region"
                                    required
                                    error={fieldState.invalid}
                                    helperText={fieldState.error?.message}
                                    value={field.value ?? null}
                                    options={stateOptions}
                                    onChange={field.onChange}
                                    disabled={!selectedCountryCode}
                                    testId="state/province/region-autocomplete"
                                    textFieldProps={{
                                        InputLabelProps: {
                                            sx: { whiteSpace: 'nowrap' },
                                        },
                                    }}
                                />
                            );
                        }}
                    />
                    <Controller
                        name="business.postalCode"
                        control={control}
                        render={({ field, fieldState }) => (
                            <TextField
                                {...field}
                                label="Zip/Postal Code"
                                required
                                error={fieldState.invalid}
                                helperText={fieldState.error?.message}
                                testId="zip/postal-code"
                            />
                        )}
                    />
                </Stack>
            </Accordion>
            <Accordion
                title="Other Info"
                variant="transparent"
                typographyProps={{
                    variant: 'subtitle1',
                }}
                // TODO: remove and make it auto-expand on form error
                defaultExpanded
            >
                <Stack spacing={3}>
                    <Controller
                        name="business.phone"
                        control={control}
                        render={({ field, fieldState }) => (
                            <TextField
                                {...field}
                                label="Phone"
                                required
                                error={fieldState.invalid}
                                placeholder="+1 (555) 555-1234"
                                helperText={fieldState.error?.message}
                                testId="business-phone"
                            />
                        )}
                    />
                    <Controller
                        name="business.fax"
                        control={control}
                        render={({ field, fieldState }) => (
                            <TextField
                                {...field}
                                label="Fax"
                                error={fieldState.invalid}
                                placeholder="+1-208-1234567"
                                helperText={fieldState.error?.message}
                                testId="business-fax"
                            />
                        )}
                    />
                    <Controller
                        name="business.dunsNumber"
                        control={control}
                        render={({ field, fieldState }) => (
                            <TextField
                                {...field}
                                label="D-U-N-S Number"
                                error={fieldState.invalid}
                                helperText={fieldState.error?.message}
                                testId="business-duns-number"
                            />
                        )}
                    />
                </Stack>
            </Accordion>
        </Stack>
    );
}

export function AccountAndContact({ control }: SupplierSignUpStepProps) {
    return (
        <Stack spacing={3} p={{ xs: 2, sm: 0 }}>
            <Accordion
                title="Create Account"
                variant="transparent"
                typographyProps={{
                    variant: 'subtitle1',
                }}
                expanded
            >
                <Stack spacing={3}>
                    <Controller
                        name="accountAndContact.firstName"
                        control={control}
                        render={({ field, fieldState }) => (
                            <TextField
                                {...field}
                                label="First Name"
                                required
                                error={fieldState.invalid}
                                helperText={fieldState.error?.message}
                                testId="primary-contact-first-name"
                            />
                        )}
                    />
                    <Controller
                        name="accountAndContact.lastName"
                        control={control}
                        render={({ field, fieldState }) => (
                            <TextField
                                {...field}
                                label="Last Name"
                                required
                                error={fieldState.invalid}
                                helperText={fieldState.error?.message}
                                testId="primary-contact-last-name"
                            />
                        )}
                    />
                    <Controller
                        name="accountAndContact.title"
                        control={control}
                        render={({ field, fieldState }) => (
                            <TextField
                                {...field}
                                label="Title"
                                error={fieldState.invalid}
                                helperText={fieldState.error?.message}
                                testId="primary-contact-title"
                            />
                        )}
                    />
                    <Controller
                        name="accountAndContact.phone"
                        control={control}
                        render={({ field, fieldState }) => (
                            <TextField
                                {...field}
                                label="Phone"
                                required
                                error={fieldState.invalid}
                                placeholder="+1 (555) 555-1234"
                                helperText={fieldState.error?.message}
                                testId="primary-contact-phone"
                            />
                        )}
                    />
                    <Controller
                        name="accountAndContact.loginEmail"
                        control={control}
                        render={({ field, fieldState }) => (
                            <TextField
                                {...field}
                                label="Login Email"
                                required
                                error={fieldState.invalid}
                                helperText={fieldState.error?.message}
                                testId="login-email"
                            />
                        )}
                    />
                    <Controller
                        name="accountAndContact.password"
                        control={control}
                        render={({ field, fieldState }) => (
                            <PasswordField
                                {...field}
                                label="Password"
                                required
                                error={fieldState.invalid}
                                helperText={fieldState.error?.message}
                                type="password"
                                testId="password"
                            />
                        )}
                    />
                    <Controller
                        name="accountAndContact.passwordConfirmation"
                        control={control}
                        render={({ field, fieldState }) => (
                            <PasswordField
                                {...field}
                                label="Password Confirmation"
                                required
                                error={fieldState.invalid}
                                helperText={fieldState.error?.message}
                                type="password"
                                testId="password-confirmation"
                            />
                        )}
                    />
                    <Stack py={4} width="100%">
                        <PasswordRules />
                    </Stack>
                </Stack>
            </Accordion>
        </Stack>
    );
}
